<script lang="ts">
	//Svelte hooks
	import { beforeUpdate, onDestroy, onMount, tick } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	import { home_data } from 'src/stores/account/user';
	import { app } from 'src/stores/session/native-bridge';
	import { edit, update, resetEditUpdateStore } from 'src/stores/ui/edit_lists';
	import { updating_devices } from 'src/stores/device-runtime/process';
	import { devices } from 'src/stores/lists/devices';
	import {
		deviceMainFilter,
		devicesFilters,
		devicesFiltersInitialState,
		resetFiltersAllDevicesList,
	} from 'src/stores/ui/filters';
	//Design system components
	import { CardGrid, Alert } from '@Shelly/shelly-design-system';
	//Types
	import type { CardGridPropsTypes, SelectOptionPropsType } from '@Shelly/shelly-design-system';
	//Stores
	import { rooms } from 'src/stores/lists/rooms';
	//Components
	import DeviceInList from '../Devices/DeviceInList.svelte';
	import DevicesToolbar from './DevicesToolbar.svelte';
	import EditDeviceWizard from '../../Devices/DeviceWizard/EditDeviceWizard.svelte';
	import EmptyDeviceList from '../EmptyDeviceList.svelte';
	import BatchWiFiModal from '../../WiFiBatchWizard/BatchWiFiModal.svelte';
	import DeleteDeviceModal from 'src/components/DevicesSettings/Settings/CommonSettings/DeviceInfo/DeleteDeviceModal.svelte';
	//Helpers
	import {
		isBluTRV,
		real_id,
		shouldExcludeBluTRVFromUpdate,
	} from 'src/helpers/interface_helpers';
	import { requestReorderDevice, getDeviceRoomList, sortBy } from '../list-helpers';

	import { updateFW } from 'src/helpers/firmware_helpers';
	import { defaultHiddenDevicesRoom } from 'src/helpers/constants';
	import {
		HiddenDevicesRoomID,
		OfflineDevicesRoomID,
		SharedAllDevicesRoomID,
		SharedRecentlyDevicesRoomID,
	} from 'src/helpers/service_rooms_helpers';
	import type { Device } from 'src/types/Device';
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	import BatchMainModal from 'src/components/BatchCommandModals/BatchMainModal.svelte';
	import BatchTransferModal from 'src/components/BatchCommandModals/BatchTransferModal.svelte';
	import BatchOfflineNotification from 'src/components/BatchCommandModals/BatchOfflineNotification.svelte';
	import { location } from 'svelte-spa-router';
	import { MAX_AI_MODEL_LIMIT } from 'src/constants';
	import { checkAIBulk } from 'src/services/http';
	import NoFilterSearchResults from './NoFilterSearchResults.svelte';
	import ListEmptyStateSurface from '../ListEmptyStateSurface.svelte';
	import { includeDeviceModal } from 'src/stores/features/inclusion';
	import {
		getAlarmsWithDevice,
		getDashboardsWithDevice,
		getGroupsWithDevice,
		getScenesWithDevice,
		getThermostatsWithDevice,
		type DeviceUsage,
	} from 'src/components/DevicesSettings/Settings/CommonSettings/DeviceInfo/device_usage_helper';
	import BatchTransferToAnotherRoomModal from 'src/components/BatchCommandModals/BatchTransferToAnotherRoomModal.svelte';
	import BatchDeleteDevicesModal from 'src/components/BatchCommandModals/BatchDeleteDevicesModal.svelte';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import TemplatesModal from 'src/components/Templates/TemplatesModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import { getSharedHomeEmptyListCopy } from 'src/helpers/houses/shared_home_messaging_helpers';
	import RoomToolbarWrapper from '../Rooms/RoomToolbarWrapper.svelte';
	import { gidBreakpointsDeviceCardWidget } from 'src/components/Dashboards/dash-helpers';

	export let list;
	export let type;
	export let id;

	const requestType = 'device';
	const flipDurationMs = 0;
	const defaultGridBreakpoints: Pick<CardGridPropsTypes, 'xl' | 'lg' | 'md' | 'sm'> = {
		xl: 350,
		lg: 290,
		sm: 270,
	};

	let dragDisabled = true;
	let showModalEdit = false;

	let showModalDelete = false;
	let showModalWifiSetup = false;
	let showTransferModal = false;
	let showBulkTransferToAnotherRoomModal = false;
	let showDeleteDevicesModal = false;
	let showSwitchModal = false;
	let showModalOfflineNotification = false;
	let showTemplatesModal = false;

	let editDevice;

	$: selectedDevicesArray = Object.values(
		$update.selectedDevices as { [key: string]: Device },
	) as Device[];

	let deviceUsage = {
		room: [],
		group: [],
		alarm: [],
		scene: [],
		dashboard: [],
		thermostat: [],
	} as DeviceUsage;

	// Flag if device is used in any of the categories
	let deviceIsUsed: boolean = false;

	let deviceRoomListDB = [];

	let currentRoute = '';
	let currentDeviceFilter = '';
	let currentRoomFilter: SelectOptionPropsType;

	// "areFiltersDisabled" is used when batch command (wi-fi or update) modal is open - filters should be disabled
	let areFiltersDisabled = false;
	let emptyDeviceListShow = false;
	let showSharedManagedModal = false;

	$: isMyDevicesRoute = type === 'devices';
	let cachedItems = [];

	// During a batch command (`$update.value === true`) we must always trust
	// the already-filtered `list` coming from `HomeListsController` — otherwise
	// the cached (pre-batch) list is briefly shown as selectable until the
	// `beforeUpdate` hook below catches up, which lets users click ineligible
	// devices for a split second.
	$: if (!($edit && currentRoute === type) || $update.value) {
		cachedItems = [...list];
	}
	$: items = ($edit && currentRoute === type && !$update.value ? cachedItems : [...list]).filter(
		(device) => !($update.value && shouldExcludeBluTRVFromUpdate($update.type, device.type)),
	);

	//have to filtrate items by by "ai_device_faliure" in cloud options and return only devices that have it set to true
	$: aiModelItems = Object.values($devices)?.filter(
		(device) => device?.cloud_options?.ai_device_failure,
	);

	// if aiModelItems + selected items are equal to max limit, then we reached the MAX_AI_MODEL_LIMIT and we should disable the checkbox of the cards
	$: reachMaximumAIModels =
		aiModelItems.length + Object.keys($update.selectedDevices).length === MAX_AI_MODEL_LIMIT &&
		$update.type === 'ai_device_failure' &&
		$update.subType === 'enable_ai_model';

	let isBulkAIModelChecked = false;
	let checkedAIDevices = {};
	let checkedAIDevicesRes: {
		isok?: boolean;
		data?: {};
		error?: string;
	} = {};

	let showEmptySearchList = false;

	const checkAIBulkDevices = async (devices) => {
		if (devices.length === 0) return;
		checkedAIDevicesRes = await checkAIBulk(devices);

		if (checkedAIDevicesRes?.isok) {
			//checkedAIDevicesRes.data returns object with keys as device id and value with type AiModelData
			checkedAIDevices = checkedAIDevicesRes.data;
		} else {
			console.error('Error while checking AI bulk devices', checkedAIDevicesRes.error);
		}
		isBulkAIModelChecked = true;
	};

	//when filter is set to get all ai devices and the sub filter is "disable_ai_model" or "manage_ai_notifications"
	//then we send request to ask what is the readiness of the AI model for each device
	//isBulkAIModelChecked is value that is set to true when we get the response from the server and stop asking for it
	//after modal is closed we set it to false so when we open it again we can ask for readiness again
	$: if (
		!isBulkAIModelChecked &&
		aiModelItems &&
		$update.type === 'ai_device_failure' &&
		($update.subType === 'disable_ai_model' || $update.subType === 'manage_ai_notifications')
	) {
		checkAIBulkDevices(aiModelItems);
	}

	$: if (!showSwitchModal) {
		isBulkAIModelChecked = false;
	}

	$: dndSettings = isMyDevicesRoute
		? {
				items: [],
				dragDisabled: true,
			}
		: {
				items,
				flipDurationMs: flipDurationMs,
				dragDisabled: dragDisabled,
			};
	$: (id, setFiltersDependOnRouteParams());

	$: deviceListType = $home_data?.['device_list']?.type || 'default';

	$: gridBreakpointWidths =
		$showNewLayout && deviceListType !== 'default'
			? gidBreakpointsDeviceCardWidget
			: defaultGridBreakpoints;

	onMount(async () => {
		await tick();

		deviceRoomListDB = getDeviceRoomList();
		currentRoute = type;
		setCurrentFilters();

		showEmptySearchList = list?.length > 0 && type === 'devices';
	});

	onDestroy(() => {
		resetEditUpdateStore();

		if (currentRoute !== type) {
			// Remove edit mode when change from Room/Group inner devices to MyDevices
			// edit.set(false);
			currentRoute = type;
			setSidePanelState({ type, id });
		}
	});

	beforeUpdate(() => {
		if (!$edit) handleEndDrag();

		// getDeviceImages();

		// Remove edit state when goes from Room devices to MyDevices
		if ($edit && currentRoute !== type) {
			currentRoute = type;
			resetEditUpdateStore();
		}

		// Do not assign `cachedItems` here while `$update.value` is true.
		// A new array every beforeUpdate dirties state and loops in Svelte 5.
		// Batch mode rebuilds `items` from `list` via the `$:` above (with BluTRV filter).

		// Update selected devices in update/change mode
		if (isMyDevicesRoute && $update.value && checkIsFilterChange()) {
			setCurrentFilters();
			updateSelectAllOnFilterChange();
		}
	});

	const setFiltersDependOnRouteParams = () => {
		resetFiltersAllDevicesList();
		const formattedID = +id;
		if (formattedID === OfflineDevicesRoomID) {
			const updatedFilters = {
				...devicesFiltersInitialState,
				device_state: $devicesFilters.device_state.map((filter) =>
					filter.id === 'offline' ? { ...filter, selected: true } : filter,
				),
			};
			devicesFilters.set(updatedFilters);
		} else if (formattedID === SharedRecentlyDevicesRoomID) {
			const updatedFilters = {
				...devicesFiltersInitialState,
				shared_devices: $devicesFilters.shared_devices.map((filter) =>
					filter.id === 'shared' ? { ...filter, selected: true } : filter,
				),
			};
			devicesFilters.set(updatedFilters);
		} else if (formattedID === HiddenDevicesRoomID) {
			devicesFilters.update((state) => {
				return {
					...state,
					rooms: [defaultHiddenDevicesRoom],
				};
			});
		} else if (formattedID === SharedAllDevicesRoomID) {
			const updatedFilters = {
				...devicesFiltersInitialState,
				shared_devices: $devicesFilters.shared_devices.map((filter) =>
					filter.id === 'all_shared' ? { ...filter, selected: true } : filter,
				),
			};
			devicesFilters.set(updatedFilters);
		}
	};

	const setCurrentFilters = () => {
		currentDeviceFilter = $deviceMainFilter;
		currentRoomFilter = $devicesFilters.rooms.find((filter) => filter.value);
	};

	const checkIsFilterChange = () => {
		let isChange = false;

		if (currentDeviceFilter !== $deviceMainFilter) {
			isChange = true;
		}

		if ($devicesFilters.rooms.find((filter) => filter.value).title !== 'all' && !isChange) {
			isChange =
				$devicesFilters.rooms.find((filter) => filter.value).value !== currentRoomFilter.value;
		}

		return isChange;
	};

	//#region Reorder
	const handleStartDrag = () => {
		if (isMyDevicesRoute) return;
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		if (isMyDevicesRoute) return;

		dragDisabled = true;
	};

	const handleReorder = (e: CustomEvent) => {
		if (isMyDevicesRoute) return;
		const reorderedItems = e.detail.items.filter(
			(device) => !($update.value && shouldExcludeBluTRVFromUpdate($update.type, device.type)),
		);
		cachedItems = reorderedItems;
		items = reorderedItems;
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		if (isMyDevicesRoute) return;

		let editedList = {};
		e.detail.items.map((v, i) => {
			delete v?.isDndShadowItem;

			editedList[v.id] = {
				...v,
				position: i,
			};
		});

		if (Object.keys(editedList).length === 0) return;
		console.log(editedList);

		const reorderedItems = [...e.detail.items].filter(
			(device) => !($update.value && shouldExcludeBluTRVFromUpdate($update.type, device.type)),
		);
		cachedItems = reorderedItems;
		items = reorderedItems;
		handleEndDrag();

		await requestReorderDevice(requestType, editedList);

		console.log('REORDERED ITEMS', items);
	};
	//#endregion Reorder

	//#region Edit
	const onCardEdit = async (device) => {
		console.log('EDIT CARD', device);
		editDevice = { ...device };
		// uploadedImagesDB = await getImages(requestType);
		showModalEdit = true;
	};

	let comingLocation = '';

	const onFinishCardEdit = async () => {
		// Manually update items list because on edit mode items doesn't update from store, dnd flicker issue
		setDevicesEditChanges();

		// TO DO chek does this will stay.
		// Redirect ot device new room

		edit.set(false);
		if (editDevice.room_id !== +id && type === 'room') {
			items = [...list];
		}
		comingLocation = $location;
		// onCloseEditModal();
		console.log('FINISH EDIT DEVICE', items, editDevice);
	};
	//#endregion Edit

	$: if (comingLocation && comingLocation !== $location) resetDrawer();

	//#region Delete
	const onCardDelete = (device) => {
		editDevice = { ...device };

		// Lists with device usage
		deviceUsage = {
			room: $rooms[editDevice.room_id] !== undefined ? [$rooms[editDevice.room_id]] : [],
			group: getGroupsWithDevice(editDevice.id),
			alarm: getAlarmsWithDevice(editDevice.id),
			scene: getScenesWithDevice(editDevice.id),
			dashboard: getDashboardsWithDevice(editDevice.id),
			thermostat: getThermostatsWithDevice(editDevice.id),
		} as DeviceUsage;

		// Flag if device is used in any of the categories
		deviceIsUsed = Object.values(deviceUsage).some((array) => array.length > 0);

		showModalDelete = true;
	};

	let deleteLoading = false;

	//#endregion Delete

	const setDevicesEditChanges = () => {
		// when we are on edit mode items are not reactive
		items = items.map((item) => {
			if (item.id === editDevice.id) {
				return editDevice;
			}
			return item;
		});

		// TO DO If we redirect when device room changes, first if check could be removed
		if (type == 'room') {
			items = items.filter((item) => {
				return item.room_id === +id;
			});
		} else if (type === 'devices') {
			items = items.sort((x, y) => {
				return sortBy(x, y, 'room_id');
			});
		}
	};

	//#region Toolbar updates functions
	const updateSelectAllOnFilterChange = () => {
		let selectAll = false;

		if (items.length <= Object.keys($update.selectedDevices).length) {
			items.forEach((item) => {
				selectAll = !!$update.selectedDevices[item.id];
			});
		}

		update.update((s) => {
			return {
				...s,
				selectAll: selectAll,
			};
		});
	};

	const onSelectAllFilter = () => {
		let currentSelectedDevices = {};

		if ($update.selectAll) {
			let allowedItems = items;
			if ($update.type === 'account_transfer_devices') {
				allowedItems = items.filter((item) => {
					return !item?.shared && !item?.shared_by_me;
				});
			}
			currentSelectedDevices = allowedItems.reduce(
				(a, device) => {
					return { ...a, [device.id]: device };
				},
				{
					...$update.selectedDevices,
				},
			);
		} else if (Object.keys($update.selectedDevices).length > 0) {
			currentSelectedDevices = $update.selectedDevices;

			items.forEach((item) => {
				delete currentSelectedDevices[item.id];
			});
		}
		update.update((s) => {
			return {
				...s,
				selectedDevices: currentSelectedDevices,
			};
		});
	};

	const onSelectCard = (e, device) => {
		const isChecked = e.detail.target.checked;
		let currentSelectedDevices = { ...$update.selectedDevices };
		let currentSelectedAll = $update.selectAll;

		// Get main device ID (remove channel suffix)
		const mainDeviceId = device.id.split('_')[0];
		const mainDevice = $devices[mainDeviceId];

		// Check if device has multiple channels
		if (
			!device.shared &&
			mainDevice?.channels_count > 1 &&
			$update.value &&
			$update.type === 'bulk_delete'
		) {
			// When selecting a channel of a multi-channel device, select/deselect all channels
			// Find all channels for this device
			const deviceChannels = items.filter((item) => item.id.split('_')[0] === mainDeviceId);

			// Select/deselect all channels
			deviceChannels.forEach((channel) => {
				if (isChecked) {
					currentSelectedDevices[channel.id] = channel;
				} else {
					delete currentSelectedDevices[channel.id];
				}
			});
		} else {
			// Single channel device
			if (isChecked) {
				currentSelectedDevices[device.id] = device;
			} else {
				delete currentSelectedDevices[device.id];
			}
		}

		// Update selectAll state
		if (!isChecked && currentSelectedAll) {
			currentSelectedAll = false;
		} else if (isChecked && !currentSelectedAll) {
			currentSelectedAll = Object.keys(currentSelectedDevices).length === items.length;
		}

		update.update((s) => ({
			...s,
			selectedDevices: currentSelectedDevices,
			selectAll: currentSelectedAll,
		}));
	};

	let checked: boolean;
	let roomConsumption: boolean;
	let accountConsumption: boolean;

	const onSubmitUpdate = () => {
		if ($update.type === 'update') {
			const currentSelectedDevices = Object.assign({}, { ...$update.selectedDevices });
			const updatingDevices = [
				...new Set(
					Object.keys($update.selectedDevices).map((id) =>
						isBluTRV($devices[id].type) ? id : real_id(id),
					),
				),
			];

			updatingDevices
				.filter((id: string) => typeof $updating_devices[id] === 'undefined')
				.map(async (id: string) => {
					const device = $devices[id];
					const res = await updateFW(device, 'stable');
					if (res?.isok) {
						const channels = device.channels_count;
						for (let i = 0; i < channels; i++) {
							if (i !== 0) {
								delete currentSelectedDevices[id + `_${i}`];
							} else {
								delete currentSelectedDevices[id];
							}
						}
						update.update((s) => {
							return {
								...s,
								selectedDevices: currentSelectedDevices,
							};
						});
					}
				});

			resetEditUpdateStore();
			resetFiltersAllDevicesList();
			areFiltersDisabled = false;
		}
		if ($update.type === 'change') showModalWifiSetup = true;
		if ($update.type === 'offline_notifications') {
			showModalOfflineNotification = true;
			return;
		}
		if ($update.type === 'account_transfer_devices') {
			showTransferModal = true;
		}
		if ($update.type === 'bulk_move_devices_to_another_room') {
			showBulkTransferToAnotherRoomModal = true;
		}
		if ($update.type === 'bulk_delete') {
			showDeleteDevicesModal = true;
		}
		if ($update.type === 'apply_settings_configuration') {
			const selectedDevices = Object.values($update.selectedDevices) as any[];
			if (selectedDevices.length > 0) {
				showTemplatesModal = true;
			}
			return;
		}
		if (
			![
				'update',
				'change',
				'account_transfer_devices',
				'bulk_move_devices_to_another_room',
				'bulk_delete',
				'apply_settings_configuration',
			].includes($update.type)
		) {
			let selectedDevices: Device[] = Object.values($update.selectedDevices) as any;
			checked = true;
			roomConsumption = true;
			accountConsumption = true;
			for (const device of selectedDevices) {
				if (
					[
						'exclude_event_log',
						'exclude_forgotten_lights',
						'measure_peak_power',
						'ai_device_failure',
					].includes($update.type)
				) {
					let condition =
						$update.type === 'exclude_forgotten_lights'
							? device.cloud_options[$update.type]
							: !device.cloud_options[$update.type];
					if (condition) {
						checked = false;
						break;
					}
				} else if ($update.type === 'consumption_calculation') {
					if (!device.no_room_cons) {
						roomConsumption = false;
					}
					if (!device.no_account_cons) {
						accountConsumption = false;
					}
					if (!roomConsumption && !accountConsumption) break;
				} else {
					if (!device[$update.type]) {
						checked = false;
						break;
					}
				}
			}

			// in every device from selectedDevices has notif_permissions but not every one has property ai_device_failure.
			// ai_device_failure is an array with two values for email and device notification. Values can be 0 and 1 for true and false respectively disable nad enable notifications
			// if every device has ai_device_failure set to 1 then we set the toggle to be on else we toggle it off
			if ($update.type === 'ai_device_failure' && $update.subType === 'manage_ai_notifications') {
				checked = selectedDevices.every(
					(device) =>
						typeof device.notif_permissions?.ai_device_failure !== 'undefined' &&
						device.notif_permissions?.ai_device_failure.includes(1),
				);
			}

			showSwitchModal = true;
		}
	};

	const onUpdateCard = async (device) => {
		let res = await updateFW(device, 'stable');
		console.log('updateFW', res);
	};

	const onAddDeviceClick = () => {
		if (isActiveHomeShared($homes.list)) {
			showSharedManagedModal = true;
			return;
		}
		if ($app) {
			includeDeviceModal.update((state) => {
				return (state = { ...state, show: true });
			});
		} else {
			emptyDeviceListShow = true;
		}
	};
	//#endregion Toolbar updates functions

	$: isSharedHomeEmpty = !showEmptySearchList && isActiveHomeShared($homes.list);
	$: sharedEmptyDevicesCopy = getSharedHomeEmptyListCopy('devices');

	$: noFilterSearchIcon = !showEmptySearchList ? 'fak fa-light-microchip-slash' : undefined;
	$: noFilterSearchTitle = isSharedHomeEmpty
		? $format(sharedEmptyDevicesCopy.titleKey, { default: sharedEmptyDevicesCopy.titleDefault })
		: !showEmptySearchList
			? $format('no_devices_yet_heading', {
					default: 'There are no devices yet',
				})
			: undefined;
	$: noFilterSearchMessage = isSharedHomeEmpty
		? $format(sharedEmptyDevicesCopy.descrKey, { default: sharedEmptyDevicesCopy.descrDefault })
		: !showEmptySearchList
			? $format('add_first_device_to_start_msg', {
					default: 'Add your first device to get started.',
				})
			: undefined;
	$: noFilterSearchButtonText =
		!showEmptySearchList && !isSharedHomeEmpty
			? $format('button_add_device', { default: 'add device' })
			: undefined;
</script>

{#if $showNewLayout && type === 'room'}
	<RoomToolbarWrapper
		showBulkActions={true}
		showDeviceListToggle
		{id}
		{currentRoute}
		{type}
		{list}
		itemsLength={items.length}
		on:select-all={onSelectAllFilter}
		on:submit-click={onSubmitUpdate}
	/>
{:else}
	<DevicesToolbar
		{type}
		{id}
		itemsLength={items.length}
		on:select-all={onSelectAllFilter}
		on:submit-click={onSubmitUpdate}
		emptyList={items.length == 0}
		bind:areFiltersDisabled
	/>
{/if}

{#if items.length == 0}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<NoFilterSearchResults
			icon={noFilterSearchIcon}
			message={noFilterSearchMessage}
			title={noFilterSearchTitle}
			buttonText={noFilterSearchButtonText}
			marginTopMd="0"
			marginTopSm="0"
			onClick={onAddDeviceClick}
		/>
	</ListEmptyStateSurface>
	<EmptyDeviceList hideMessage bind:showModal={emptyDeviceListShow} />
{:else}
	{#if reachMaximumAIModels}
		<Alert
			type={'danger'}
			heading={$format('reached_maximum_number_of_ai_numbers', {
				default: 'You have reached the maximum number of AI Models',
			})}
			isHeadingBold
			class="mb-s"
		/>
	{/if}
	<CardGrid
		editMode={$edit}
		xl={gridBreakpointWidths.xl}
		lg={gridBreakpointWidths.lg}
		sm={gridBreakpointWidths.sm}
		{dndSettings}
		on:consider={handleReorder}
		on:finalize={handleReorderEnd}
	>
		{#each items as device, _ (device)}
			<div class:dnd-placeholder={device.isDndShadowItem}>
				<DeviceInList
					{device}
					aiModelState={checkedAIDevices[device.id]}
					disableSelection={reachMaximumAIModels}
					on:handle-drag-start={handleStartDrag}
					on:handle-drag-end={handleEndDrag}
					on:edit={() => onCardEdit(device)}
					on:delete={() => onCardDelete(device)}
					on:device-select={(e) => onSelectCard(e, device)}
					on:device-update={() => onUpdateCard(device)}
					{type}
				/>
			</div>
		{/each}
	</CardGrid>
{/if}

{#if showModalEdit}
	<EditDeviceWizard bind:editDevice bind:showModalEdit on:finish={onFinishCardEdit} />
{/if}

<!-- Delete modal -->
{#if showModalDelete}
	<DeleteDeviceModal
		bind:showModalDelete
		device={editDevice}
		{deviceIsUsed}
		{deviceUsage}
		on:close={() => {
			showModalDelete = false;
		}}
		on:delete={(e) => {
			deleteLoading = e.detail.deleteLoading;
		}}
	/>
{/if}
<!-- Delete modal -->

{#if showModalWifiSetup}
	<BatchWiFiModal
		bind:showModal={showModalWifiSetup}
		on:finish={onSubmitUpdate}
		bind:areFiltersDisabled
	/>
{/if}

{#if showSwitchModal}
	<BatchMainModal
		{checked}
		{roomConsumption}
		{accountConsumption}
		bind:showModal={showSwitchModal}
		bind:areFiltersDisabled
	/>
{/if}

{#if showTransferModal}
	<BatchTransferModal bind:showModal={showTransferModal} bind:areFiltersDisabled />
{/if}

{#if showDeleteDevicesModal}
	<BatchDeleteDevicesModal bind:showModal={showDeleteDevicesModal} />
{/if}

{#if showBulkTransferToAnotherRoomModal}
	<BatchTransferToAnotherRoomModal
		bind:showModal={showBulkTransferToAnotherRoomModal}
		bind:areFiltersDisabled
		bind:currentRoomId={id}
	/>
{/if}

{#if showModalOfflineNotification}
	<BatchOfflineNotification
		selectedDevices={$update.selectedDevices}
		bind:showModal={showModalOfflineNotification}
	/>
{/if}

{#if showTemplatesModal}
	<TemplatesModal
		bind:showModal={showTemplatesModal}
		mode="bulk"
		device={list.filter((item) => $update.selectedDevices[item.id])}
	/>
{/if}

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="add_device" />
